const API_URL = process.env.REACT_APP_BACKEND_URL || '';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('token');
  }

  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          this.clearToken();
          window.location.href = '/login';
          throw new Error('Unauthorized');
        }
        const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Auth
  async login(email, password) {
    const data = await this.request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async register(email, username, password) {
    const data = await this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, username, password }),
    });
    this.setToken(data.access_token);
    return data;
  }

  async getMe() {
    return this.request('/api/auth/me');
  }

  async updateProfile(data) {
    return this.request('/api/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async changeOwnPassword(currentPassword, newPassword) {
    return this.request('/api/auth/change-password', {
      method: 'PATCH',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // Users (Admin only)
  async getUsers() {
    return this.request('/api/users');
  }

  async updateUser(userId, data) {
    return this.request(`/api/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(userId) {
    return this.request(`/api/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Roadmaps
  async getRoadmaps() {
    return this.request('/api/roadmaps');
  }

  async getRoadmap(id) {
    return this.request(`/api/roadmaps/${id}`);
  }

  async createRoadmap(data) {
    return this.request('/api/roadmaps', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteRoadmap(id) {
    return this.request(`/api/roadmaps/${id}`, {
      method: 'DELETE',
    });
  }

  // Items
  async getItems(roadmapId) {
    return this.request(`/api/roadmaps/${roadmapId}/items`);
  }

  async getItem(id) {
    return this.request(`/api/items/${id}`);
  }

  async createItem(data) {
    return this.request('/api/items', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateItem(id, data) {
    return this.request(`/api/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteItem(id) {
    return this.request(`/api/items/${id}`, {
      method: 'DELETE',
    });
  }

  async searchItems(roadmapId, filters) {
    return this.request(`/api/roadmaps/${roadmapId}/items/search`, {
      method: 'POST',
      body: JSON.stringify(filters),
    });
  }

  // Comments
  async getComments(itemId) {
    return this.request(`/api/items/${itemId}/comments`);
  }

  async createComment(data) {
    return this.request('/api/comments', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Metadata
  async getMetadata(type = null) {
    const query = type ? `?type=${type}` : '';
    return this.request(`/api/metadata${query}`);
  }

  async createMetadata(data) {
    return this.request('/api/metadata', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMetadata(id, data) {
    return this.request(`/api/metadata/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteMetadata(id) {
    return this.request(`/api/metadata/${id}`, {
      method: 'DELETE',
    });
  }

  // Saved Views
  async getSavedViews(roadmapId) {
    return this.request(`/api/roadmaps/${roadmapId}/views`);
  }

  async createSavedView(roadmapId, data) {
    return this.request(`/api/roadmaps/${roadmapId}/views`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteSavedView(viewId) {
    return this.request(`/api/views/${viewId}`, {
      method: 'DELETE',
    });
  }

  // Baselines
  async getBaselines(roadmapId) {
    return this.request(`/api/roadmaps/${roadmapId}/baselines`);
  }

  async createBaseline(roadmapId, data) {
    return this.request(`/api/roadmaps/${roadmapId}/baselines`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBaseline(baselineId) {
    return this.request(`/api/baselines/${baselineId}`);
  }

  // Notifications
  async getNotifications() {
    return this.request('/api/notifications');
  }

  async markNotificationRead(notificationId) {
    return this.request(`/api/notifications/${notificationId}/read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/api/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  // Sync
  async getSyncConnections() {
    return this.request('/api/sync/connections');
  }

  async createSyncConnection(data) {
    return this.request('/api/sync/connections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async triggerSync(connectionId, direction = 'inbound') {
    return this.request(`/api/sync/connections/${connectionId}/sync?direction=${direction}`, {
      method: 'POST',
    });
  }

  async getSyncLogs() {
    return this.request('/api/sync/logs');
  }

  // Share
  async createShareLink(roadmapId, options = {}) {
    const params = new URLSearchParams();
    if (options.password) params.append('password', options.password);
    if (options.expires_days) params.append('expires_days', options.expires_days);
    
    return this.request(`/api/roadmaps/${roadmapId}/share?${params.toString()}`, {
      method: 'POST',
    });
  }

  async getSharedRoadmap(token, password = null) {
    const params = password ? `?password=${password}` : '';
    return this.request(`/api/share/${token}${params}`);
  }

  // Activity Log
  async getActivityLog(roadmapId, limit = 50) {
    return this.request(`/api/roadmaps/${roadmapId}/activity?limit=${limit}`);
  }

  // Goals
  async getGoals() {
    return this.request('/api/goals');
  }

  async createGoal(data) {
    return this.request('/api/goals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateGoal(goalId, data) {
    return this.request(`/api/goals/${goalId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteGoal(goalId) {
    return this.request(`/api/goals/${goalId}`, {
      method: 'DELETE',
    });
  }

  // OKRs
  async getOKRs() {
    return this.request('/api/okrs');
  }

  async createOKR(data) {
    return this.request('/api/okrs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateOKR(okrId, data) {
    return this.request(`/api/okrs/${okrId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteOKR(okrId) {
    return this.request(`/api/okrs/${okrId}`, {
      method: 'DELETE',
    });
  }

  // Themes
  async getThemes() {
    return this.request('/api/themes');
  }

  async createTheme(data) {
    return this.request('/api/themes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteTheme(themeId) {
    return this.request(`/api/themes/${themeId}`, {
      method: 'DELETE',
    });
  }

  // Dependencies
  async getItemDependencies(itemId) {
    return this.request(`/api/items/${itemId}/dependencies`);
  }

  async createDependency(data) {
    return this.request('/api/dependencies', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteDependency(depId) {
    return this.request(`/api/dependencies/${depId}`, {
      method: 'DELETE',
    });
  }

  // Releases
  async getReleases() {
    return this.request('/api/releases');
  }

  async createRelease(data) {
    return this.request('/api/releases', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRelease(releaseId, data) {
    return this.request(`/api/releases/${releaseId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteRelease(releaseId) {
    return this.request(`/api/releases/${releaseId}`, {
      method: 'DELETE',
    });
  }

  async getReleaseItems(releaseId) {
    return this.request(`/api/releases/${releaseId}/items`);
  }

  // Milestones
  async getMilestones(roadmapId) {
    return this.request(`/api/roadmaps/${roadmapId}/milestones`);
  }

  async createMilestone(roadmapId, data) {
    return this.request(`/api/roadmaps/${roadmapId}/milestones`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMilestone(milestoneId) {
    return this.request(`/api/milestones/${milestoneId}`, {
      method: 'DELETE',
    });
  }

  // Scenarios
  async getScenarios(roadmapId) {
    return this.request(`/api/roadmaps/${roadmapId}/scenarios`);
  }

  async createScenario(roadmapId, data) {
    return this.request(`/api/roadmaps/${roadmapId}/scenarios`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async promoteScenario(scenarioId) {
    return this.request(`/api/scenarios/${scenarioId}/promote`, {
      method: 'POST',
    });
  }

  // Admin - Create User
  async createUser(data) {
    return this.request('/api/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Admin - Change Password
  async changeUserPassword(userId, newPassword) {
    return this.request(`/api/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ password: newPassword }),
    });
  }
}

const api = new ApiClient();
export default api;
